<template>
  <div class="align-items-center justify-content-center" style="display: flex">
    <div style="width: 100%; max-width: 1200px; min-width: 320px">
      <h2 style="text-align: center; margin-bottom: 12px">Mailversand</h2>

      <Message severity="info" :closable="false" style="margin-bottom: 16px">
        Für jede versendete Mail steht hier die Antwort des Mailservers samt Message-ID.
        <strong>Angenommen</strong> heißt: der Mailserver hat die Mail in die Warteschlange
        gestellt. <strong>Zugestellt</strong> oder <strong>unzustellbar</strong> steht erst
        da, wenn der Empfänger-Mailserver eine Zustellbenachrichtigung zurückgemeldet hat -
        das tun nicht alle Anbieter.
      </Message>

      <h4 v-if="error && error.length > 0" style="color: red">{{ error }}</h4>

      <div v-if="summary" class="summary-row">
        <div
          v-for="tile in summaryTiles"
          :key="tile.label"
          class="summary-tile"
          :class="{
            selectable: tile.status,
            selected: isStatusSelected(tile.status),
          }"
          :style="isStatusSelected(tile.status) ? { borderColor: tile.color } : {}"
          :role="tile.status ? 'checkbox' : null"
          :tabindex="tile.status ? 0 : null"
          :aria-checked="tile.status ? isStatusSelected(tile.status) : null"
          @click="toggleStatus(tile.status)"
          @keyup.enter="toggleStatus(tile.status)"
          @keyup.space="toggleStatus(tile.status)"
        >
          <div class="summary-value" :style="{ color: tile.color }">{{ tile.value }}</div>
          <div class="summary-label">{{ tile.label }}</div>
        </div>
      </div>

      <div v-if="selectedStatuses.length > 0" class="summary-reset">
        <Button
          label="Auswahl aufheben"
          icon="pi pi-times"
          severity="secondary"
          text
          size="small"
          @click="clearStatusSelection"
        />
      </div>

      <div class="filter-row">
        <span>
          <label for="mailLogDays">Zeitraum</label>
          <Dropdown
            id="mailLogDays"
            v-model="days"
            :options="dayOptions"
            optionLabel="label"
            optionValue="value"
            @change="reload"
          />
        </span>
        <span style="flex: 1 1 200px">
          <label for="mailLogSearch">Suche</label>
          <InputText
            id="mailLogSearch"
            v-model="search"
            placeholder="Empfänger, Betreff, Benutzer, Message-ID"
            style="width: 100%"
            @keyup.enter="reload"
          />
        </span>
        <Button label="Neu laden" icon="pi pi-refresh" :disabled="busy" @click="reload" />
        <Button
          label="Zustellbenachrichtigungen abrufen"
          icon="pi pi-inbox"
          severity="secondary"
          :disabled="busy || !bounceCheckEnabled"
          @click="checkBounces"
        />
      </div>

      <Message
        v-if="bounceResult"
        :severity="bounceResult.severity"
        :closable="true"
        style="margin-bottom: 12px"
        @close="bounceResult = null"
      >
        {{ bounceResult.text }}
      </Message>

      <DataTable
        :value="mails"
        :loading="loading"
        v-model:expandedRows="expandedRows"
        dataKey="id"
        stripedRows
        paginator
        :rows="25"
        :rowsPerPageOptions="[25, 50, 100]"
        responsiveLayout="scroll"
      >
        <Column expander headerStyle="width: 3rem" />
        <Column header="Zeitpunkt" sortable field="sentAt">
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.sentAt) }}
          </template>
        </Column>
        <Column field="recipient" header="Empfänger" sortable />
        <Column header="Anlass">
          <template #body="slotProps">
            <div>{{ purposeText(slotProps.data.purpose) }}</div>
            <div v-if="slotProps.data.reference" style="font-size: 0.85em; color: #6c757d">
              {{ slotProps.data.reference }}
            </div>
          </template>
        </Column>
        <Column header="Status" sortable field="status">
          <template #body="slotProps">
            <span :style="{ color: statusColor(slotProps.data.status), fontWeight: 500 }">
              {{ statusText(slotProps.data.status) }}
            </span>
            <div
              v-if="slotProps.data.deliveryStatusCode"
              style="font-size: 0.85em; color: #6c757d"
            >
              {{ slotProps.data.deliveryStatusCode }}
            </div>
          </template>
        </Column>
        <Column header="Zugang genutzt">
          <template #body="slotProps">
            <span
              v-if="slotProps.data.usedAfterSend === true"
              style="color: #2e7d32"
              title="Nach dem Versand hat sich dieser Benutzer angemeldet"
            >
              ja
            </span>
            <span
              v-else-if="slotProps.data.usedAfterSend === false"
              style="color: #b3261e"
              title="Seit dem Versand kein Login"
            >
              nein
            </span>
            <span v-else style="color: #6c757d">&ndash;</span>
          </template>
        </Column>
        <Column header="Warteschlange">
          <template #body="slotProps">
            <code v-if="slotProps.data.queueId">{{ slotProps.data.queueId }}</code>
          </template>
        </Column>

        <template #expansion="slotProps">
          <div style="padding: 8px 12px">
            <table class="detail-table">
              <tbody>
                <tr>
                  <th>Betreff</th>
                  <td>{{ slotProps.data.subject }}</td>
                </tr>
                <tr>
                  <th>Message-ID</th>
                  <td><code>{{ slotProps.data.messageId }}</code></td>
                </tr>
                <tr>
                  <th>Antwort des Mailservers</th>
                  <td>
                    <code v-if="slotProps.data.smtpResponse">{{
                      slotProps.data.smtpResponse
                    }}</code>
                    <span v-else style="color: #6c757d; font-style: italic">
                      keine Antwort protokolliert
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Dauer</th>
                  <td>{{ slotProps.data.durationMillis }} ms</td>
                </tr>
                <tr v-if="slotProps.data.errorMessage">
                  <th>Fehler</th>
                  <td style="color: #b3261e">{{ slotProps.data.errorMessage }}</td>
                </tr>
                <tr v-if="slotProps.data.lastLoggedIn">
                  <th>Letzter Login des Benutzers</th>
                  <td>{{ formatDateTime(slotProps.data.lastLoggedIn) }}</td>
                </tr>
                <tr v-if="slotProps.data.deliveryReportAt">
                  <th>Rückmeldung am</th>
                  <td>{{ formatDateTime(slotProps.data.deliveryReportAt) }}</td>
                </tr>
                <tr v-if="slotProps.data.deliveryAction">
                  <th>Meldung</th>
                  <td>
                    {{ slotProps.data.deliveryAction }}
                    <span v-if="slotProps.data.deliveryStatusCode">
                      ({{ slotProps.data.deliveryStatusCode }})
                    </span>
                  </td>
                </tr>
                <tr v-if="slotProps.data.reportingMta">
                  <th>Meldender Mailserver</th>
                  <td>{{ slotProps.data.reportingMta }}</td>
                </tr>
                <tr v-if="slotProps.data.diagnosticCode">
                  <th>Begründung</th>
                  <td>{{ slotProps.data.diagnosticCode }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template #empty>
          <span style="color: #6c757d; font-style: italic">
            Für den gewählten Zeitraum ist kein Versand protokolliert.
          </span>
        </template>
      </DataTable>

      <div style="margin-top: 24px">
        <Button
          v-if="!unusedAccounts"
          label="Nie benutzte Zugänge anzeigen"
          icon="pi pi-user-minus"
          severity="secondary"
          :disabled="loadingUnused"
          @click="loadUnusedAccounts"
        />
        <div v-else>
          <h3 style="margin-bottom: 8px">
            Nie benutzte Zugänge ({{ unusedAccounts.length }})
          </h3>
          <Message severity="info" :closable="false" style="margin-bottom: 12px">
            Diese Zugänge sind aktiv, wurden aber noch nie zum Anmelden benutzt. Das ist
            kein Beweis für eine verlorene Mail - manche nutzen das Programm einfach nicht.
            Als Kandidatenliste zum Nachfragen ist es aber der schnellste Weg, und wenn
            sich die Adressen auf einen Anbieter häufen, liegt das Problem beim Versand.
          </Message>
          <DataTable
            :value="unusedAccounts"
            stripedRows
            paginator
            :rows="25"
            responsiveLayout="scroll"
          >
            <Column field="userName" header="Benutzer" sortable />
            <Column field="contactPersonName" header="Kontaktperson" sortable />
            <Column field="mailAddress" header="Adresse" sortable />
            <Column header="Letzte Zugangsdaten-Mail" sortable field="lastCredentialsMailAt">
              <template #body="slotProps">
                <span v-if="slotProps.data.lastCredentialsMailAt">
                  {{ formatDateTime(slotProps.data.lastCredentialsMailAt) }}
                </span>
                <span v-else style="color: #6c757d; font-style: italic">
                  nicht protokolliert
                </span>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import FailureHandler from "../../services/ServerConnection/FailureHandler";
import { useMenuService } from "../../services/MenuService/UseMenuService";

const STATUS_TEXTS = {
  SENT: "angenommen",
  DELIVERED: "zugestellt",
  DELAYED: "verzögert",
  BOUNCED: "unzustellbar",
  FAILED: "Versand fehlgeschlagen",
};

const STATUS_COLORS = {
  SENT: "#0b57d0",
  DELIVERED: "#2e7d32",
  DELAYED: "#b26a00",
  BOUNCED: "#b3261e",
  FAILED: "#b3261e",
};

const PURPOSE_TEXTS = {
  CREDENTIALS: "Zugangsdaten",
  CONVERSATION_NOTIFICATION: "Postfach-Hinweis",
  OTHER: "Sonstige",
};

export default {
  name: "MailLogView",
  components: {
    DataTable,
    Column,
    Button,
    Dropdown,
    InputText,
    Message,
  },
  data() {
    return {
      mails: [],
      summary: null,
      expandedRows: [],
      loading: false,
      busy: false,
      error: "",
      days: 30,
      selectedStatuses: [],
      search: "",
      bounceResult: null,
      bounceCheckEnabled: false,
      unusedAccounts: null,
      loadingUnused: false,
      dayOptions: [
        { label: "7 Tage", value: 7 },
        { label: "30 Tage", value: 30 },
        { label: "90 Tage", value: 90 },
        { label: "1 Jahr", value: 365 },
      ],
    };
  },
  computed: {
    summaryTiles() {
      if (!this.summary) {
        return [];
      }
      return [
        { label: "gesamt", value: this.summary.total, color: "#3f3f46", status: null },
        { label: "angenommen", value: this.summary.sent, color: STATUS_COLORS.SENT, status: "SENT" },
        {
          label: "zugestellt",
          value: this.summary.delivered,
          color: STATUS_COLORS.DELIVERED,
          status: "DELIVERED",
        },
        {
          label: "verzögert",
          value: this.summary.delayed,
          color: STATUS_COLORS.DELAYED,
          status: "DELAYED",
        },
        {
          label: "unzustellbar",
          value: this.summary.bounced,
          color: STATUS_COLORS.BOUNCED,
          status: "BOUNCED",
        },
        {
          label: "fehlgeschlagen",
          value: this.summary.failed,
          color: STATUS_COLORS.FAILED,
          status: "FAILED",
        },
      ];
    },
  },
  mounted() {
    const menuService = useMenuService();
    menuService.setMainMenuItemNames(["home"]);
    this.reload();
  },
  methods: {
    reload() {
      this.loadSummary();
      this.loadMails();
    },
    isStatusSelected(status) {
      return status !== null && this.selectedStatuses.indexOf(status) >= 0;
    },
    toggleStatus(status) {
      if (!status) {
        return;
      }
      if (this.isStatusSelected(status)) {
        this.selectedStatuses = this.selectedStatuses.filter((each) => each !== status);
      } else {
        this.selectedStatuses = this.selectedStatuses.concat([status]);
      }
      this.loadMails();
    },
    clearStatusSelection() {
      this.selectedStatuses = [];
      this.loadMails();
    },
    loadMails() {
      this.loading = true;
      this.error = "";
      ServerConnection.get("admin/maillog" + this.queryParameters())
        .then((response) => {
          this.mails = response.data;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    loadSummary() {
      ServerConnection.get("admin/maillog/summary?days=" + this.days)
        .then((response) => {
          this.summary = response.data;
          this.bounceCheckEnabled = response.data.bounceCheckEnabled;
        })
        .catch((error) => {
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    checkBounces() {
      this.busy = true;
      this.bounceResult = null;
      ServerConnection.post("admin/maillog/checkbounces", {})
        .then((response) => {
          this.busy = false;
          this.bounceResult = this.describeBounceCheck(response.data);
          this.reload();
        })
        .catch((error) => {
          this.busy = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    loadUnusedAccounts() {
      this.loadingUnused = true;
      ServerConnection.get("admin/maillog/unusedaccounts")
        .then((response) => {
          this.unusedAccounts = response.data;
          this.loadingUnused = false;
        })
        .catch((error) => {
          this.loadingUnused = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    describeBounceCheck(result) {
      if (result.failure) {
        return { severity: "error", text: "Das Postfach ließ sich nicht auswerten: " + result.failure };
      }
      if (!result.executed) {
        return { severity: "warn", text: "Die Auswertung des Postfachs ist abgeschaltet." };
      }
      return {
        severity: "success",
        text:
          result.inspected +
          " Nachrichten geprüft, " +
          result.matched +
          " Rückmeldungen zugeordnet, " +
          result.unmatched +
          " ohne Treffer.",
      };
    },
    queryParameters() {
      var parameters = "?days=" + this.days;
      this.selectedStatuses.forEach((status) => {
        parameters += "&status=" + status;
      });
      if (this.search && this.search.trim().length > 0) {
        parameters += "&search=" + encodeURIComponent(this.search.trim());
      }
      return parameters;
    },
    statusText(status) {
      return STATUS_TEXTS[status] || status;
    },
    statusColor(status) {
      return STATUS_COLORS[status] || "#3f3f46";
    },
    purposeText(purpose) {
      return PURPOSE_TEXTS[purpose] || purpose || "";
    },
    formatDateTime(value) {
      if (!value) {
        return "";
      }
      var text = String(value).replace("T", " ");
      var parts = text.split(" ");
      var dateParts = parts[0].split("-");
      if (dateParts.length !== 3) {
        return text;
      }
      var time = parts.length > 1 ? " " + parts[1].substring(0, 5) : "";
      return dateParts[2] + "." + dateParts[1] + "." + dateParts[0] + time;
    },
  },
};
</script>

<style scoped>
.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.summary-tile {
  flex: 1 1 120px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  text-align: center;
}

.summary-tile.selectable {
  cursor: pointer;
  user-select: none;
}

.summary-tile.selectable:hover {
  background-color: #f4f6f8;
}

.summary-tile.selected {
  border-width: 2px;
  padding: 7px 11px;
  background-color: #eef2f7;
}

.summary-reset {
  margin-top: -8px;
  margin-bottom: 16px;
}

.summary-value {
  font-size: 1.4em;
  font-weight: 600;
}

.summary-label {
  font-size: 0.85em;
  color: #6c757d;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}

.filter-row label {
  display: block;
  font-size: 0.85em;
  color: #6c757d;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table th {
  text-align: left;
  vertical-align: top;
  padding: 3px 12px 3px 0;
  width: 200px;
  color: #6c757d;
  font-weight: 500;
}

.detail-table td {
  padding: 3px 0;
  word-break: break-word;
}
</style>
